import Input from "../../../components/custom/forms/Input";
import CheckBox from "../../../components/custom/forms/CheckBox";
import { useContext, useEffect, useMemo, useState, useImperativeHandle, forwardRef } from "react";
import { contextProvider } from "../../../../context/ContextProvider";
import Alerts from "../../../components/Alerts/Alerts";
import { Validate } from "../../../../utils/EventoUtils";
import ReactQuill from "react-quill";
import "./quill.snow.css";
import RestPrivateAntesala from "../../../../helpers/RestPrivateApi";

const Que = forwardRef(({ setForm, form, setValidation, validation, categorias, materialPendiente, setMaterialPendiente }, ref) => {
  const [materialDifusion, setMaterialDifusion] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingPrincipal, setIsDraggingPrincipal] = useState(false);
  const [uploadingMaterial, setUploadingMaterial] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState(null);
  const context = useContext(contextProvider);
  const { FORMs, VALIDATIONs, URLs } = context;
  const { VALIDATION_FORM_EVENTO } = VALIDATIONs;

  // Cargar material de difusión al montar el componente
  useEffect(() => {
    if (form.id && form.id[0]) {
      cargarMaterialDifusion();
    }
  }, [form.id]);

  const cargarMaterialDifusion = async () => {
    setLoadingData(true);
    try {
      const response = await RestPrivateAntesala({
        url: `/evento-imagenes?evento_id=${form.id[0]}`,
        method: "get",
        setLoading: () => { }
      });


      if (response.status === 200 && response.data.data) {
        const materialesFiltrados = response.data.data.filter(
          (item) => item.descripcion === "material de difusion"
        );
        setMaterialDifusion(materialesFiltrados);
      }
    } catch (error) {
      console.error("Error al cargar material de difusión:", error);
      Alerts({
        msg: "Error",
        title: "No se pudo cargar el material de difusión",
        code: "500",
        type: "error",
      });
    } finally {
      setLoadingData(false);
    }
  };

  // Exponer función para subir material pendiente
  useImperativeHandle(ref, () => ({
    subirMaterialPendiente: async (eventoId, files) => {
      if (!files || files.length === 0) {
        return; // Material de difusión opcional
      }

      if (files.length > 2) {
        throw new Error("No puedes subir más de 2 imágenes de difusión");
      }

      setUploadingMaterial(true);

      for (const file of files) {
        const formData = new FormData();
        formData.append("evento_id", eventoId);
        formData.append("imagen", file);
        formData.append("descripcion", "material de difusion");

        try {
          await RestPrivateAntesala({
            url: "/evento-imagenes",
            formData: formData,
            method: "post",
            setLoading: () => { },
          });
        } catch (error) {
          console.error("Error al subir material pendiente:", error);
        }
      }

      setUploadingMaterial(false);
    },
    validarMaterialDifusion: () => {
      const total = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
      if (total > 2) {
        return { valid: false, message: "No puedes subir más de 2 imágenes de difusión" };
      }
      return { valid: true, message: "" };
    }
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length === 0) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    await subirImagenesMaterial(imageFiles);
  };

  const subirImagenesMaterial = async (files) => {
    // Validar límite de 2 archivos
    const totalActual = form.id && form.id[0] ? materialDifusion.length : materialPendiente.length;
    const totalNuevo = totalActual + files.length;

    if (totalNuevo > 2) {
      return Alerts({
        msg: "Warning",
        title: `Solo puedes subir un máximo de 2 archivos. Actualmente tienes ${totalActual}.`,
        code: "400",
        type: "error",
      });
    }

    // Si el evento no existe, guardar archivos como pendientes
    if (!form.id || !form.id[0]) {
      setMaterialPendiente((prev) => [...prev, ...files]);
      Alerts({
        msg: "Info",
        title: `${files.length}`,
        code: "200",
        type: "success",
      });
      return;
    }

    // Si el evento ya existe, subir directamente
    setUploadingMaterial(true);

    for (const file of files) {
      const formData = new FormData();
      formData.append("evento_id", form.id[0]);
      formData.append("archivo", file);
      formData.append("descripcion", "material de difusion");

      try {
        const response = await RestPrivateAntesala({
          url: "/evento-imagenes",
          formData: formData,
          method: "post",
          setLoading: setUploadingMaterial,
        });

        if (response.status === 200 || response.status === 201) {
          Alerts({
            msg: "Success",
            title: "Imagen subida correctamente",
            code: "200",
            type: "success",
          });
        } else {
          Alerts({
            msg: "Error",
            title: response.data.msg || "Error al subir la imagen",
            code: "400",
            type: "error",
          });
        }
      } catch (error) {
        Alerts({
          msg: "Error",
          title: "Error al subir la imagen",
          code: "400",
          type: "error",
        });
      }
    }

    await cargarMaterialDifusion();
  };

  const eliminarMaterialDifusion = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar esta imagen?")) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append("_method", "DELETE");

      const response = await RestPrivateAntesala({
        url: `/evento-imagen/${id}`,
        formData: formData,
        method: "post",
      });

      if (response.status === 200) {
        Alerts({
          msg: "Success",
          title: "Imagen eliminada correctamente",
          code: "200",
          type: "success",
        });
        await cargarMaterialDifusion();
      } else {
        Alerts({
          msg: "Error",
          title: response.data.msg || "Error al eliminar la imagen",
          code: "400",
          type: "error",
        });
      }
    } catch (error) {
      Alerts({
        msg: "Error",
        title: "Error al eliminar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const descargarImagen = async (url, nombre) => {
    try {
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.click();
    } catch (error) {
      console.error('Error al descargar la imagen:', error);
      Alerts({
        msg: "Error",
        title: "Error al descargar la imagen",
        code: "400",
        type: "error",
      });
    }
  };

  const onClick = (e, value) => {
    switch (e.target.name) {
      case "ficha_tecnica-delete":
        {
          let input = form;
          let result = input.ficha_tecnica[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            ficha_tecnica: [result, "required"],
          });

          Alerts({
            msg: "Success",
            title: "Participante eliminado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "ficha_tecnica":
        {
          const input_validation = Validate(
            form,
            JSON.parse(JSON.stringify(VALIDATION_FORM_EVENTO)),
            "ficha_tecnica"
          );
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["ficha_tecnica-nombre"][0],
            rol: form["ficha_tecnica-rol"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            "ficha_tecnica-nombre": ["", "required"],
            "ficha_tecnica-rol": ["", "required"],
          });

          Alerts({
            msg: "Success",
            title: "Participante agregado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "link":
        {
          // Validar formato de URL
          const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          if (!urlPattern.test(form["link-url"][0])) {
            return Alerts({
              msg: "Warning",
              title: "Por favor ingresa una URL válida",
              code: "400",
              type: "error",
            });
          }

          const input_validation = Validate(form, validation, "link");
          setValidation(input_validation);
          //Validaciones
          if (input_validation.error.status) {
            return Alerts({
              msg: "Warning",
              title: input_validation.error.msg,
              code: "201",
              type: "error",
            });
          }

          let input = form;
          let result = input[e.target.name][0];
          result.push({
            nombre: form["link-nombre"][0],
            url: form["link-url"][0],
          });

          input[e.target.name][0] = result;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
            "link-nombre": ["", "required"],
            "link-url": ["", "required"],
          });

          Alerts({
            msg: "Success",
            title: "Link agregado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
      case "link-delete":
        {
          let input = form;
          let result = input.link[0];

          delete result[value];
          result = result.filter((i) => i != null);

          setForm({
            ...form,
            link: [result, "required"],
          });

          Alerts({
            msg: "Success",
            title: "Link eliminado correctamente",
            code: "200",
            type: "success",
          });
        }
        break;
    }
  };

  // Handlers para drag and drop de imagen principal
  const handleDragOverPrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(true);
  };

  const handleDragLeavePrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(false);
  };

  const handleDropPrincipal = (e) => {
    e.preventDefault();
    setIsDraggingPrincipal(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (!imageFile) {
      return Alerts({
        msg: "Warning",
        title: "Solo se permiten archivos de imagen",
        code: "400",
        type: "error",
      });
    }

    if (files.length > 1) {
      Alerts({
        msg: "Info",
        title: "Solo puedes subir una imagen principal. Se tomará la primera.",
        code: "200",
        type: "info",
      });
    }

    procesarImagenPrincipal(imageFile);
  };

  const procesarImagenPrincipal = (file) => {
    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      return Alerts({
        msg: "Warning",
        title: "La imagen no debe superar los 5MB",
        code: "400",
        type: "error",
      });
    }

    let input = form;
    input["imagen"][0] = file;
    input["imagen_url"][0] = URL.createObjectURL(file);

    setForm({
      ...form,
      imagen: input["imagen"],
      imagen_url: input["imagen_url"],
    });

    Alerts({
      msg: "Success",
      title: "Imagen principal cargada correctamente",
      code: "200",
      type: "success",
    });
  };

  const onChange = (e, value) => {
    switch (e.target.name) {
      case "atp":
      case "adultos":
      case "infancias": {
        let input = form;

        if (input[e.target.name][0] == 'SI') {
          input[e.target.name][0] = 'NO';
        } else {
          input[e.target.name][0] = 'SI';
        }

        setForm({
          ...form,
          [e.target.name]: input[e.target.name],
        });
      } break;
      case "nombre":
        {
          // Validar que no contenga "/" ni símbolos extraños
          const value = e.target.value;
          const invalidCharsRegex = /[\/\\<>:"|?*\x00-\x1F]/g;

          if (invalidCharsRegex.test(value)) {
            return Alerts({
              msg: "Warning",
              title: "El nombre del evento no puede contener los caracteres: / \\ < > : \" | ? *",
              code: "400",
              type: "error",
            });
          }

          let input = form;
          input[e.target.name][0] = value;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "duracion":
      case "nombre_grupo":
      case "clase":
      case "grupo":
      case "link-nombre":
      case "link-url":
      case "ficha_tecnica-nombre":
      case "ficha_tecnica-rol":
        {
          let input = form;
          input[e.target.name][0] = e.target.value;

          setForm({
            ...form,
            [e.target.name]: input[e.target.name],
          });
        }
        break;
      case "imagen":
        {
          const file = e.target.files[0];
          if (file) {
            procesarImagenPrincipal(file);
          }
        }
        break;
      case "categorias":
        {
          let input = form;
          let result = form.categorias[0];
          if (
            result.indexOf(categorias[e.target.id].id) < 0 &&
            result.length < 3
          ) {
            result.push(categorias[e.target.id].id);
          } else {
            delete result[result.indexOf(categorias[e.target.id].id)];
            result = result.filter((i) => i != null);
          }

          input["categorias"][0] = result;

          setForm({
            ...form,
            categorias: input["categorias"],
          });
        }
        break;
    }
  };


  const handleChange = (value) => {
    // Validar que no contenga emojis
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{FE00}-\u{FE0F}\u{1F004}\u{1F0CF}\u{1F18E}\u{3030}\u{2B50}\u{2B55}\u{231A}-\u{231B}\u{23E9}-\u{23EC}\u{23F0}\u{23F3}\u{25FD}-\u{25FE}\u{2614}-\u{2615}\u{2648}-\u{2653}\u{267F}\u{2693}\u{26A1}\u{26AA}-\u{26AB}\u{26BD}-\u{26BE}\u{26C4}-\u{26C5}\u{26CE}\u{26D4}\u{26EA}\u{26F2}-\u{26F3}\u{26F5}\u{26FA}\u{26FD}\u{2705}\u{270A}-\u{270B}\u{2728}\u{274C}\u{274E}\u{2753}-\u{2755}\u{2757}\u{2795}-\u{2797}\u{27B0}\u{27BF}\u{2B1B}-\u{2B1C}]/gu;

    if (emojiRegex.test(value)) {
      Alerts({
        msg: "Warning",
        title: "No se permiten emojis en la descripción del evento",
        code: "400",
        type: "error",
      });
      return;
    }

    let input = form;
    input['descripcion'][0] = value;
    setForm({
      ...form,
      descripcion: input['descripcion'],
    });
  }

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {
        video: function () {
          const url = prompt('Ingresa la URL del video de YouTube:');
          if (url) {
            let embedUrl = url;

            // Convertir URL de YouTube a formato embed
            if (url.includes('youtube.com/watch?v=')) {
              const videoId = url.split('v=')[1].split('&')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            } else if (url.includes('youtu.be/')) {
              const videoId = url.split('youtu.be/')[1].split('?')[0];
              embedUrl = `https://www.youtube.com/embed/${videoId}`;
            }

            const range = this.quill.getSelection(true);
            this.quill.insertEmbed(range.index, 'video', embedUrl);
            this.quill.setSelection(range.index + 1);
          }
        }
      }
    }
  }), []);


  return (
    <div className="container-md  w-100">
      {/* Modal de previsualización de imagen */}
      {imagePreviewModal && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.8)" }}
          onClick={() => setImagePreviewModal(null)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-body p-0 position-relative">
                <button
                  className="btn btn-close btn-close-white position-absolute top-0 end-0 m-3"
                  style={{ zIndex: 1000 }}
                  onClick={() => setImagePreviewModal(null)}
                ></button>
                <img
                  src={imagePreviewModal}
                  alt="Vista previa"
                  className="w-100 rounded"
                  style={{ maxHeight: "90vh", objectFit: "contain" }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        className="border  p-3 mb-4"
        style={{ borderRadius: "1rem" }}
      >
        <div className="row">
          <div className="col-md-12 mb-3 px-1">
            <h5 className="GothamBold d-flex align-items-center">
              Información del evento
              <span className="badge bg-danger ms-2" style={{ fontSize: "0.7rem" }}>Obligatorio</span>
            </h5>

            <hr />

            <div className="row">
              {/* Preview de la imagen principal */}
              <div className="col-md-5 mb-3">
                <div
                  className="position-relative overflow-hidden"
                  style={{
                    height: "350px",
                    borderRadius: "12px",
                    border: form.imagen_url[0] ? "3px solid #212529" : "3px dashed #dee2e6",
                    transition: "all 0.3s ease",
                    backgroundColor: "#f8f9fa"
                  }}
                >
                  {form.imagen_url[0] ? (
                    <>
                      <img
                        src={form.imagen_url[0]}
                        alt="Vista previa de imagen principal"
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                      {/* Overlay con acciones */}
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          background: "linear-gradient(to bottom, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)",
                          opacity: 0,
                          transition: "opacity 0.3s ease"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = 0}
                      >
                        <div className="text-center">
                          <button
                            className="btn btn-light btn-lg me-2 mb-2"
                            onClick={() => setImagePreviewModal(form.imagen_url[0])}
                            type="button"
                          >
                            <i className="bi bi-zoom-in me-2"></i>
                            Ampliar
                          </button>
                          <label className="btn btn-primary btn-lg mb-2" style={{ cursor: "pointer" }}>
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Cambiar
                            <input
                              type="file"
                              className="d-none"
                              accept="image/*"
                              name="imagen"
                              onChange={onChange}
                            />
                          </label>
                        </div>
                      </div>
                      {/* Badge de estado */}
                      <div
                        className="position-absolute top-0 end-0 m-3"
                      >
                        <span className="badge bg-success" style={{ fontSize: "0.85rem", padding: "8px 12px" }}>
                          <i className="bi bi-check-circle me-1"></i>
                          Imagen cargada
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                      <div className="text-center">
                        <i className="bi bi-image" style={{ fontSize: "4rem", opacity: 0.3 }}></i>
                        <p className="mt-3 mb-0">Sin imagen</p>
                        <small>Arrastra una imagen o usa el selector</small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Zona de drag and drop */}
              <div className="col-md-7">
                <label className="form-label fw-medium d-flex align-items-center mb-3" style={{ fontSize: "14px" }}>
                  <i className="bi bi-image me-2 text-dark"></i>
                  Imagen principal <span className="text-danger ms-1">*</span>
                  <span
                    className="badge bg-info text-dark ms-2"
                    data-bs-toggle="tooltip"
                    title="Tamaño recomendado: 1080 x 1080 píxeles, formato cuadrado 1:1"
                    style={{ cursor: "help", fontSize: "0.75rem" }}
                  >
                    <i className="bi bi-info-circle me-1"></i>
                    1:1 (1080 x 1080 px)
                  </span>
                </label>

                <div
                  onDragOver={handleDragOverPrincipal}
                  onDragLeave={handleDragLeavePrincipal}
                  onDrop={handleDropPrincipal}
                  className={`border rounded p-4 text-center ${
                    isDraggingPrincipal ? "border-primary bg-light" : "border-secondary"
                  }`}
                  style={{
                    borderStyle: "dashed",
                    borderWidth: "3px",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    minHeight: "200px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <input
                    type="file"
                    id="imagen-principal-input"
                    accept="image/*"
                    name="imagen"
                    onChange={onChange}
                    style={{ display: "none" }}
                  />
                  <label
                    htmlFor="imagen-principal-input"
                    className="w-100 h-100 d-flex flex-column align-items-center justify-content-center"
                    style={{ cursor: "pointer" }}
                  >
                    {isDraggingPrincipal ? (
                      <>
                        <i className="bi bi-cloud-upload-fill text-dark" style={{ fontSize: "3.5rem" }}></i>
                        <p className="mt-3 mb-0 text-dark fw-bold">¡Suelta la imagen aquí!</p>
                      </>
                    ) : (
                      <>
                        <i className="bi bi-cloud-upload text-secondary" style={{ fontSize: "3.5rem" }}></i>
                        <p className="mt-3 mb-2 text-dark fw-bold">
                          Arrastra tu imagen aquí
                        </p>
                        <small className="text-muted mt-3 d-block">
                          Formatos: JPG, PNG, GIF · Máx. 5MB
                        </small>
                      </>
                    )}
                  </label>
                </div>

                {/* Mensaje de validación */}
                {validation.imagen_url.status && (
                  <div className="alert alert-danger mt-3 d-flex align-items-center" style={{ fontSize: "14px" }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {validation.imagen_url.msg}
                  </div>
                )}

                {/* Información adicional */}
                <div className="mt-3">
                  <div className="card bg-light border-0">
                    <div className="card-body p-3">
                      <h6 className="card-title mb-2" style={{ fontSize: "0.9rem" }}>
                        <i className="bi bi-lightbulb text-warning me-2"></i>
                        Recomendaciones
                      </h6>
                      <ul className="mb-0 ps-3" style={{ fontSize: "0.85rem" }}>
                        <li>Usa imágenes de alta calidad y bien iluminadas</li>
                        <li>Formato cuadrado 1:1 (1080 x 1080 px) para mejor visualización</li>
                        <li>Evita texto o logos muy pequeños</li>
                        <li>El tamaño máximo permitido es 5MB</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8 col-12 mb-3">
              <label className="form-label fw-medium" style={{ fontSize: "14px" }}>
                <i className="bi bi-card-heading me-2 text-dark"></i>
                Nombre del evento <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${validation.nombre.status ? 'is-invalid' : ''}`}
                name="nombre"
                value={form.nombre[0]}
                onChange={onChange}
                placeholder="Ej: La casa de Bernarda Alba"
                style={{ borderRadius: "8px" }}
              />
              <small className="text-muted d-block mt-1">
                <i className="bi bi-info-circle me-1"></i>
                No se permiten los caracteres: / \ &lt; &gt; : " | ? *
              </small>
              {validation.nombre.status && (
                <div className="invalid-feedback d-block">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {validation.nombre.msg}
                </div>
              )}
            </div>

            <div className="col-md-8 col-12 mb-3">
              <Input
                onChange={onChange}
                label={"Nombre del grupo (opcional)"}
                name={"nombre_grupo"}
                type={"text"}
                value={form.nombre_grupo[0]}
                svg={""}
                validate={validation.nombre_grupo}
                disabled={false}
              />
            </div>

            <div className="col-md-4 col-12 mb-3">
              <Input
                onChange={onChange}
                label={"Duracion del evento en minutos"}
                name={"duracion"}
                type={"number"}
                value={form.duracion[0]}
                svg={""}
                validate={validation.duracion}
                disabled={false}
              />
            </div>

            <div className="col-md-4 col-12 mb-3">
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-tags me-2 text-dark"></i>
                <span className="text-muted fw-bold">
                  Categorías <span className="text-danger">*</span>
                </span>
                <span className="badge bg-info text-dark ms-2" style={{ fontSize: "0.7rem" }}>
                  Máx. 3
                </span>
              </div>
              <div className="border rounded p-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
                {categorias
                  ? categorias.map((c, index) => (
                    <CheckBox
                      onChange={onChange}
                      label={c.nombre}
                      name={"categorias"}
                      type={"checkbox"}
                      checked={
                        form.categorias[0].indexOf(c.id) < 0 ? false : true
                      }
                      uniqueKey={index}
                      disabled={form.categorias[0].length >= 3 && form.categorias[0].indexOf(c.id) < 0}
                    />
                  ))
                  : <div className="text-center text-muted py-3">
                      <i className="bi bi-hourglass-split"></i> Cargando categorías...
                    </div>}
                <div className="text-end mt-2">
                  <small className={`badge ${form.categorias[0].length === 0 ? 'bg-danger' : form.categorias[0].length >= 3 ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {form.categorias[0].length} / 3 seleccionadas
                  </small>
                </div>
              </div>

              {validation.categorias.status ? (
                <div className="text-danger mt-2">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {validation.categorias.msg}
                </div>
              ) : (
                ""
              )}
            </div>

            <div className="col-md-4 col-12 mb-3">
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-people me-2 text-dark"></i>
                <span className="fw-bold">Tipo de público <span className="text-danger">*</span></span>
                <span
                  className="ms-2 text-info"
                  style={{ cursor: "help" }}
                  data-bs-toggle="tooltip"
                  title="Selecciona al menos una opción de público objetivo"
                >
                  <i className="bi bi-info-circle"></i>
                </span>
              </div>
              <div className="border rounded p-3">
                <div class="form-check mb-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="atp"
                    id="atp-check"
                    value={"ATP"}
                    checked={form.atp[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label" for="atp-check">
                    ATP (Apto Todo Público)
                  </label>
                </div>
                <div class="form-check mb-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="adultos"
                    id="adultos-check"
                    value={"ADULTOS"}
                    checked={form.adultos[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label" for="adultos-check">
                    Adultos
                  </label>
                </div>
                <div class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    name="infancias"
                    id="infancias-check"
                    value={"INFANCIAS"}
                    checked={form.infancias[0] == "SI" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                  <label class="form-check-label" for="infancias-check">
                    Infancias
                  </label>
                </div>
              </div>
              {validation.grupo.status ? (
                <div className="text-danger mt-2">
                  <i className="bi bi-exclamation-triangle me-1"></i>
                  {validation.grupo.msg}
                </div>
              ) : (
                ""
              )}
            </div>

            <div className="col-md-4 col-12 mb-3 px-md-5 text-nowrap d-none">
              Tipo de evento
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  ONLINE
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"ONLINE"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "ONLINE" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              <div class="form-check">
                <label class="form-check-label" for="flexRadioDefault1">
                  PRESENCIAL
                  <input
                    class="form-check-input"
                    type="radio"
                    name="clase"
                    value={"PRESENCIAL"}
                    id="flexRadioDefault1"
                    checked={form.clase[0] == "PRESENCIAL" ? true : false}
                    onChange={(event) => onChange(event)}
                  />
                </label>
              </div>
              {validation.clase.status ? (
                <div className="text-danger">{validation.clase.msg}</div>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="p-3 mb-4 border"
        style={{ borderRadius: "1rem", border: '1px solid' }}
      >
        <div className="row">
          <div className="col-md-12 mb-3">
            <h5 className="d-flex align-items-center">
              <i className="bi bi-file-text me-2 text-dark"></i>
              Describe tu evento
              <span className="badge bg-danger ms-2" style={{ fontSize: "0.7rem" }}>Obligatorio</span>
            </h5>
            <p className="text-muted small mb-3">
              <i className="bi bi-lightbulb me-1"></i>
              Proporciona una descripción detallada y atractiva de tu evento. Puedes usar formato, agregar imágenes y videos.
            </p>
            <hr />
            <div style={{ minHeight: "300px" }}>
              <ReactQuill
                value={form?.descripcion[0]}
                onChange={handleChange}
                theme="snow"
                modules={modules}
                placeholder="Escribe aquí la descripción del evento..."
              />
            </div>
            <div className="mt-2 d-flex justify-content-between align-items-center">
              <small className="text-muted">
                <i className="bi bi-exclamation-circle me-1"></i>
                No se permiten emojis en la descripción
              </small>
              {form?.descripcion[0] && (
                <small className={`badge ${form.descripcion[0].length > 100 ? 'bg-success' : 'bg-warning text-dark'}`}>
                  {form.descripcion[0].length > 100 ? '✓ Descripción completa' : 'Agrega más detalles'}
                </small>
              )}
            </div>
          </div>
          {validation.descripcion.status ? (
            <div className="text-danger">
              <i className="bi bi-exclamation-triangle me-1"></i>
              {validation.descripcion.msg}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>

      <div
        className="border  p-3 mb-4"
        style={{ borderRadius: "1rem" }}
      >
        <div className="row">
          <div className="col-md-6 col-12 mb-3">
            <h5 className="d-flex align-items-center">
              <i className="bi bi-person-lines-fill me-2 text-dark"></i>
              Ficha técnica
            </h5>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Ingresa el nombre y rol de cada participante del evento (actores, directores, músicos, etc.)
            </p>
            <hr />
            <Input
              onChange={onChange}
              label={"Nombre de participante"}
              name={"ficha_tecnica-nombre"}
              type={"text"}
              value={form["ficha_tecnica-nombre"][0]}
              icon={'<i class="bi bi-person"></i>'}
              validate={validation["ficha_tecnica-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"Rol de participante"}
              name={"ficha_tecnica-rol"}
              type={"text"}
              value={form["ficha_tecnica-rol"][0]}
              icon={'<i class="bi bi-briefcase"></i>'}
              validate={validation["ficha_tecnica-rol"]}
              disabled={false}
            />
            <label className="btn btn-custom-naranja fw-normal GothamBold text-white w-100 mt-1" style={{ cursor: "pointer" }}>
              <i className="bi bi-plus-circle me-2"></i>
              Añadir participante
              <button
                className="d-none"
                name="ficha_tecnica"
                onClick={(event) => onClick(event)}
              ></button>
            </label>
            {validation.ficha_tecnica.status ? (
              <div className="text-danger mt-2">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {validation.ficha_tecnica.msg}
              </div>
            ) : (
              ""
            )}
          </div>
          <div className="col-md-6">
            <h6 className="text-muted mb-3">
              <i className="bi bi-list-ul me-2"></i>
              Participantes agregados ({form.ficha_tecnica[0].length})
            </h6>
            {form.ficha_tecnica[0].length === 0 ? (
              <div className="text-center text-muted py-5 border rounded bg-light">
                <i className="bi bi-person-plus" style={{ fontSize: "3rem", opacity: 0.3 }}></i>
                <p className="mt-2">Aún no hay participantes agregados</p>
              </div>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <ul class="list-group">
                  {form.ficha_tecnica[0].map((f, index) => (
                    <li class="list-group-item border text-muted d-flex justify-content-between align-items-center shadow-sm mb-2 hover-shadow" style={{ transition: "all 0.2s ease" }}>
                      <div className="d-flex align-items-center">
                        <div className="bg-primary text-white rounded-circle me-3 d-flex align-items-center justify-content-center" style={{ width: "35px", height: "35px", fontSize: "0.85rem" }}>
                          {index + 1}
                        </div>
                        <div>
                          <div className="text-black fw-bold">{f.nombre}</div>
                          <small className="text-muted">
                            <i className="bi bi-briefcase me-1"></i>
                            {f.rol}
                          </small>
                        </div>
                      </div>
                      <label
                        className="btn btn-outline-danger btn-sm"
                        style={{ cursor: "pointer" }}
                      >
                        <i class="bi bi-trash"></i>
                        <button
                          className="d-none"
                          name="ficha_tecnica-delete"
                          onClick={(event) => onClick(event, index)}
                        ></button>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border  p-3 mb-4" style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <h5 className="d-flex align-items-center">
              <i className="bi bi-link-45deg me-2 text-dark"></i>
              Links para mi evento
              <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>Opcional</span>
            </h5>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Agrega enlaces a redes sociales, videos, sitios web relacionados al evento
            </p>
            <hr />
            <Input
              onChange={onChange}
              label={"Nombre del enlace"}
              name={"link-nombre"}
              type={"text"}
              value={form["link-nombre"][0]}
              icon={'<i class="bi bi-tag"></i>'}
              validate={validation["link-nombre"]}
              disabled={false}
            />
            <Input
              onChange={onChange}
              label={"URL completa (https://...)"}
              name={"link-url"}
              type={"url"}
              value={form["link-url"][0]}
              icon={'<i class="bi bi-globe"></i>'}
              validate={validation["link-url"]}
              disabled={false}
            />
            <label className="btn btn-custom-naranja fw-normal GothamBold text-white w-100 mt-1" style={{ cursor: "pointer" }}>
              <i className="bi bi-plus-circle me-2"></i>
              Añadir link
              <button
                className="d-none"
                name="link"
                onClick={(event) => onClick(event)}
              ></button>
            </label>
          </div>

          <div className="col-md-6">
            <h6 className="text-muted mb-3">
              <i className="bi bi-list-ul me-2"></i>
              Enlaces agregados ({form.link[0].length})
            </h6>
            {form.link[0].length === 0 ? (
              <div className="text-center text-muted py-5 border rounded bg-light">
                <i className="bi bi-link" style={{ fontSize: "3rem", opacity: 0.3 }}></i>
                <p className="mt-2">Aún no hay enlaces agregados</p>
              </div>
            ) : (
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                <ul class="list-group">
                  {form.link[0].map((l, index) => (
                    <li class="list-group-item border text-muted d-flex justify-content-between align-items-center shadow-sm mb-2">
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-1">
                          <i className="bi bi-link-45deg text-dark me-2"></i>
                          <span className="text-black fw-bold">{l.nombre}</span>
                        </div>
                        <a
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small text-dark text-decoration-none d-block text-truncate"
                          style={{ maxWidth: "300px" }}
                        >
                          <i className="bi bi-box-arrow-up-right me-1"></i>
                          {l.url}
                        </a>
                      </div>
                      <label
                        className="btn btn-outline-danger btn-sm ms-2"
                        style={{ cursor: "pointer" }}
                      >
                        <i class="bi bi-trash"></i>
                        <button
                          className="d-none"
                          name="link-delete"
                          onClick={(event) => onClick(event, index)}
                        ></button>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border p-3 mb-4" style={{ borderRadius: "1rem" }}>
        <div className="row">
          <div className="col-md-12 mb-3">
            <h5 className="d-flex align-items-center">
              <i className="bi bi-images me-2 text-dark"></i>
              Material de Difusión
              <span className="badge bg-secondary ms-2" style={{ fontSize: "0.7rem" }}>Opcional</span>
            </h5>
            <p className="text-muted small">
              <i className="bi bi-info-circle me-1"></i>
              Puedes subir hasta 2 imágenes o fotografías del espectáculo sin logo ni texto
            </p>
            <hr />

            {loadingData && (
              <div className="alert alert-info d-flex align-items-center">
                <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                Cargando material de difusión...
              </div>
            )}

            {/* Zona de drag and drop */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border rounded p-5 text-center ${isDragging ? "border-primary bg-light" : "border-secondary"
                }`}
              style={{
                borderStyle: "dashed",
                borderWidth: "2px",
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
            >
              <input
                type="file"
                id="material-difusion-input"
                multiple
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                disabled={
                  (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                }
              />
              <label
                htmlFor="material-difusion-input"
                style={{ cursor: "pointer", width: "100%" }}
              >
                <div>
                  <i
                    className="bi bi-cloud-upload"
                    style={{ fontSize: "3rem", color: "#6c757d" }}
                  ></i>
                  <p className="mt-3 mb-0 text-muted">
                    {uploadingMaterial
                      ? "Subiendo imágenes..."
                      : isDragging
                        ? "Suelta las imágenes aquí"
                        : "Arrastra imágenes aquí o haz clic para seleccionar"}
                  </p>
                  <small className="text-muted">
                    Formatos: JPG, PNG, GIF. Puedes subir múltiples archivos.
                  </small>
                </div>
              </label>
            </div>

            {/* Contador de archivos */}
            <div className="mt-3 d-flex justify-content-between align-items-center">
              <small className={`badge ${
                (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) === 0
                  ? 'bg-secondary'
                  : (form.id && form.id[0] ? materialDifusion.length : materialPendiente.length) >= 2
                    ? 'bg-success'
                    : 'bg-info text-dark'
              }`}>
                <i className="bi bi-image me-1"></i>
                {form.id && form.id[0] ? materialDifusion.length : materialPendiente.length} / 2 imágenes cargadas
              </small>
              {uploadingMaterial && (
                <small className="badge bg-warning text-dark">
                  <span className="spinner-border spinner-border-sm me-1"></span>
                  Subiendo...
                </small>
              )}
            </div>

            {/* Vista previa de archivos pendientes (para eventos nuevos) */}
            {!form.id[0] && materialPendiente.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes pendientes ({materialPendiente.length}/2):</h6>
                <div className="row">
                  {materialPendiente.map((file, index) => (
                    <div key={index} className="col-md-3 col-sm-6 mb-3">
                      <div
                        className="border shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={URL.createObjectURL(file)}
                          alt="Material pendiente"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => {
                            const url = URL.createObjectURL(file);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = file.name;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(url);
                          }}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() =>
                            setMaterialPendiente((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                        <div
                          className="badge bg-warning text-dark"
                          style={{
                            position: "absolute",
                            bottom: "5px",
                            left: "5px",
                          }}
                        >
                          Pendiente
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Galería de imágenes subidas */}
            {form.id[0] && materialDifusion.length > 0 && (
              <div className="mt-4">
                <h6>Imágenes cargadas ({materialDifusion.length}/2):</h6>
                <div className="row">
                  {materialDifusion.map((material) => (
                    <div
                      key={material.id}
                      className="col-md-3 col-sm-6 mb-3"
                    >
                      <div
                        className="border shadow-sm"
                        style={{ position: "relative" }}
                      >
                        <img
                          src={material.url}
                          alt="Material de difusión"
                          className="card-img-top"
                          style={{
                            height: "200px",
                            objectFit: "cover",
                          }}
                        />
                        <button
                          onClick={() => descargarImagen(material.url, `material-difusion-${material.id}.jpg`)}
                          className="btn btn-primary btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "50px",
                          }}
                        >
                          <i className="bi bi-download"></i>
                        </button>
                        <button
                          onClick={() => eliminarMaterialDifusion(material.id)}
                          className="btn btn-danger btn-sm"
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                          }}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Que;
